import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { BUTTON_THEME_COLORS } from '../../lib/constants';
import { mockResponseService } from './lib/mock-response-service';
import type {
  PreviewDevice,
  PreviewDisplayState,
  PreviewMessage,
  WidgetPreviewConfig,
} from './types';
import { ChatWindow } from './chat-window';
import { WelcomePreview } from './welcome-preview';
import { WidgetTrigger } from './widget-trigger';
import styles from './widget-preview.module.scss';

interface WidgetPreviewProps {
  config: WidgetPreviewConfig;
  device?: PreviewDevice;
  displayState?: PreviewDisplayState;
  showContactForm?: boolean;
  showTyping?: boolean;
  showFormError?: boolean;
  resetSignal?: number;
  clearSignal?: number;
  demoEvent?: string | null;
  onDisplayStateChange?: (state: PreviewDisplayState) => void;
  onContactFormVisibilityChange?: (visible: boolean) => void;
}

type PreviewStyle = CSSProperties & Record<`--${string}`, string>;

const cloneMessages = (messages: PreviewMessage[]) => messages.map((message) => ({ ...message }));

export const WidgetPreview = ({
  config,
  device = 'desktop',
  displayState,
  showContactForm,
  showTyping = false,
  showFormError = false,
  resetSignal = 0,
  clearSignal = 0,
  demoEvent = null,
  onDisplayStateChange,
  onContactFormVisibilityChange,
}: WidgetPreviewProps) => {
  const [internalDisplayState, setInternalDisplayState] = useState<PreviewDisplayState>('trigger');
  const [messages, setMessages] = useState(() => cloneMessages(config.demo.initialMessages));
  const [runtimeTyping, setRuntimeTyping] = useState(false);
  const [recommendationsVisible, setRecommendationsVisible] = useState(true);
  const [hasSubmittedContact, setHasSubmittedContact] = useState(false);
  const [internalFormVisible, setInternalFormVisible] = useState(
    config.demo.showContactFormInitially,
  );
  const sequenceRef = useRef(0);
  const messageIdRef = useRef(config.demo.initialMessages.length);
  const responseControllerRef = useRef<AbortController | null>(null);
  const recommendationsFrameRef = useRef<number | null>(null);
  const latestConfigRef = useRef(config);
  latestConfigRef.current = config;
  const initialMessagesSignature = config.demo.initialMessages
    .map((message) => `${message.id}:${message.sender}:${message.text}`)
    .join('|');
  const effectiveDisplayState = displayState ?? internalDisplayState;
  const contactFormVisible = showContactForm ?? internalFormVisible;
  const typingVisible = showTyping || (runtimeTyping && config.demo.showTyping);
  const inputLocked = showTyping || runtimeTyping;

  const setDisplayState = (state: PreviewDisplayState) => {
    setInternalDisplayState(state);
    onDisplayStateChange?.(state);
  };

  const setContactFormVisible = (visible: boolean) => {
    setInternalFormVisible(visible);
    onContactFormVisibilityChange?.(visible);
  };

  const abortPendingResponse = () => {
    responseControllerRef.current?.abort();
    responseControllerRef.current = null;
  };

  const cancelRecommendationsFrame = () => {
    if (recommendationsFrameRef.current === null) return;
    window.cancelAnimationFrame(recommendationsFrameRef.current);
    recommendationsFrameRef.current = null;
  };

  const showRecommendationsAfterRender = () => {
    cancelRecommendationsFrame();
    recommendationsFrameRef.current = window.requestAnimationFrame(() => {
      setRecommendationsVisible(true);
      recommendationsFrameRef.current = null;
    });
  };

  useEffect(
    () => () => {
      responseControllerRef.current?.abort();
      if (recommendationsFrameRef.current !== null) {
        window.cancelAnimationFrame(recommendationsFrameRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    abortPendingResponse();
    cancelRecommendationsFrame();
    const latestConfig = latestConfigRef.current;
    sequenceRef.current = 0;
    messageIdRef.current = latestConfig.demo.initialMessages.length;
    setMessages(cloneMessages(latestConfig.demo.initialMessages));
    setRuntimeTyping(false);
    setRecommendationsVisible(true);
    setHasSubmittedContact(false);
    setInternalFormVisible(latestConfig.demo.showContactFormInitially);
  }, [resetSignal]);

  useEffect(() => {
    abortPendingResponse();
    cancelRecommendationsFrame();
    const latestConfig = latestConfigRef.current;
    sequenceRef.current = 0;
    messageIdRef.current = latestConfig.demo.initialMessages.length;
    setMessages(cloneMessages(latestConfig.demo.initialMessages));
    setRuntimeTyping(false);
    setRecommendationsVisible(true);
  }, [config.lang]);

  useEffect(() => {
    const localizedMessages = new Map(
      latestConfigRef.current.demo.initialMessages.map((message) => [message.id, message]),
    );

    setMessages((previous) => {
      const synchronizedMessages = previous
        .filter(
          (message) =>
            message.id !== 'demo-assistant-form-answer' || localizedMessages.has(message.id),
        )
        .map((message) => {
          const localizedMessage = localizedMessages.get(message.id);
          return localizedMessage ? { ...localizedMessage } : message;
        });
      const contactFormAnswer = localizedMessages.get('demo-assistant-form-answer');

      return contactFormAnswer &&
        !synchronizedMessages.some((message) => message.id === contactFormAnswer.id)
        ? [...synchronizedMessages, { ...contactFormAnswer }]
        : synchronizedMessages;
    });
  }, [initialMessagesSignature]);

  useEffect(() => {
    if (clearSignal === 0) return;
    abortPendingResponse();
    cancelRecommendationsFrame();
    setMessages([]);
    setRuntimeTyping(false);
    setRecommendationsVisible(true);
    setHasSubmittedContact(false);
    setInternalFormVisible(false);
  }, [clearSignal]);

  useEffect(() => {
    if (displayState || internalDisplayState !== 'trigger') return;
    const timeoutId = window.setTimeout(
      () => setInternalDisplayState('welcome'),
      Math.max(0, config.preview.showDelaySeconds * 1000),
    );
    return () => window.clearTimeout(timeoutId);
  }, [config.preview.showDelaySeconds, displayState, internalDisplayState]);

  useEffect(() => {
    if (
      demoEvent &&
      config.contactForm.mode === 'by_events' &&
      config.contactForm.triggerEvents.includes(demoEvent)
    ) {
      setInternalFormVisible(true);
    }
  }, [config.contactForm.mode, config.contactForm.triggerEvents, demoEvent]);

  const requestAssistantResponse = async (text: string, showFormAfter = false) => {
    abortPendingResponse();
    const controller = new AbortController();
    responseControllerRef.current = controller;
    setRuntimeTyping(true);

    try {
      const response = await mockResponseService({
        text,
        strategy: config.demo.responseStrategy,
        responses: config.demo.botResponses,
        keywordRules: config.demo.keywordRules,
        sequenceIndex: sequenceRef.current,
        fixedResponseIndex: config.demo.fixedResponseIndex,
        delayMs: config.demo.responseDelayMs,
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      sequenceRef.current += 1;
      messageIdRef.current += 1;
      setMessages((previous) => [
        ...previous,
        { id: `runtime-${messageIdRef.current}`, sender: 'assistant', text: response },
      ]);
      if (showFormAfter) {
        setContactFormVisible(true);
      } else {
        showRecommendationsAfterRender();
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      messageIdRef.current += 1;
      setMessages((previous) => [
        ...previous,
        {
          id: `runtime-${messageIdRef.current}`,
          sender: 'assistant',
          text: config.translations.sendMessageFailed,
        },
      ]);
      if (showFormAfter) {
        setContactFormVisible(true);
      } else {
        showRecommendationsAfterRender();
      }
    } finally {
      if (!controller.signal.aborted) setRuntimeTyping(false);
      if (responseControllerRef.current === controller) responseControllerRef.current = null;
    }
  };

  const handleSend = (text: string) => {
    const trimmedText = text.trim();
    if (!trimmedText || inputLocked || contactFormVisible) return;
    cancelRecommendationsFrame();
    setRecommendationsVisible(false);
    messageIdRef.current += 1;
    setMessages((previous) => [
      ...previous,
      { id: `runtime-${messageIdRef.current}`, sender: 'user', text: trimmedText },
    ]);

    const userMessageCount = messages.filter((message) => message.sender === 'user').length + 1;
    const shouldShowForm =
      !hasSubmittedContact &&
      config.contactForm.mode === 'by_messages' &&
      userMessageCount >= config.contactForm.showAfterMessages;

    if (shouldShowForm) {
      if (config.contactForm.showAfterAnswer) {
        void requestAssistantResponse(trimmedText, true);
        return;
      }
      setContactFormVisible(true);
      return;
    }

    void requestAssistantResponse(trimmedText);
  };

  const handleContactSubmit = () => {
    const lastUserMessage = [...messages]
      .reverse()
      .find((message) => message.sender === 'user')?.text;
    cancelRecommendationsFrame();
    setRecommendationsVisible(false);
    setContactFormVisible(false);
    setHasSubmittedContact(true);
    messageIdRef.current += 1;
    setMessages((previous) => [
      ...previous,
      {
        id: `runtime-${messageIdRef.current}`,
        sender: 'assistant',
        text: config.contactForm.messageBeforeForm,
      },
    ]);
    if (!config.contactForm.showAfterAnswer) {
      void requestAssistantResponse(lastUserMessage ?? '');
    }
  };

  const position = config.position[device];
  const triggerColors = useMemo(() => {
    if (config.appearance.buttonTheme === 'dark') {
      return BUTTON_THEME_COLORS.dark;
    }
    if (config.appearance.buttonTheme === 'custom') {
      return {
        background: config.appearance.buttonBackgroundColor,
        text: config.appearance.buttonTextColor,
      };
    }
    return BUTTON_THEME_COLORS.light;
  }, [config.appearance]);

  const style: PreviewStyle = {
    '--preview-brand': config.appearance.brandColor,
    '--preview-font': `${config.appearance.fontFamily}, 'Inter Tight', sans-serif`,
    '--preview-trigger-background': triggerColors.background,
    '--preview-trigger-text': triggerColors.text,
    '--preview-trigger-width': `${config.trigger.widthPx}px`,
    '--preview-trigger-height': `${config.trigger.heightPx}px`,
    '--preview-trigger-border-width': `${config.trigger.borderWidthPx}px`,
    '--preview-trigger-border-color': config.trigger.borderColor,
    '--preview-widget-width': `${config.frame.widthPx}px`,
    '--preview-widget-height': `${config.frame.heightPx}px`,
    '--preview-widget-border-width': `${config.frame.borderWidthPx}px`,
    '--preview-widget-border-color': config.frame.borderColor,
    '--preview-side-offset': `${position.sideOffset}${position.unit}`,
    '--preview-vertical-offset': `${position.bottomOffset}${position.unit}`,
  };

  return (
    <div
      className={styles.widgetRoot}
      data-device={device}
      data-placement={position.placement}
      style={style}
    >
      <div
        className={`${styles.widgetContainer} ${effectiveDisplayState === 'open' ? styles.expanded : styles.collapsed}`}
      >
        {effectiveDisplayState === 'open' ? (
          <ChatWindow
            config={config}
            messages={messages}
            showContactForm={contactFormVisible}
            showTyping={typingVisible}
            showFormError={showFormError}
            showRecommendations={recommendationsVisible && !inputLocked}
            inputLocked={inputLocked}
            resetSignal={resetSignal}
            onCollapse={() => setDisplayState('trigger')}
            onSend={handleSend}
            onContactSubmit={handleContactSubmit}
            onContactFormClose={() => setContactFormVisible(false)}
          />
        ) : (
          <WidgetTrigger config={config} onOpen={() => setDisplayState('open')} />
        )}
      </div>
      {effectiveDisplayState === 'welcome' && (
        <WelcomePreview
          config={config}
          onOpen={() => setDisplayState('open')}
          onClose={() => setDisplayState('trigger')}
          onAction={(action) => {
            setDisplayState('open');
            window.setTimeout(() => handleSend(action), 0);
          }}
        />
      )}
    </div>
  );
};
